import clsx from 'clsx';
import Image from 'next/image';
import React from 'react';
import Slide from 'react-reveal/Slide';

import appleStoreImg from '@/assets/images/apple-store.png';
import playNowHeroMobile from '@/assets/images/careers.png';
import googlePlayImg from '@/assets/images/google-play.png';
import playNowHero from '@/assets/images/play-now-hero.png';
import SectionTitle from '@/components/Core/SectionTitle/SectionTitle';
import type { WaitlistFormData } from '@/components/Forms/WaitlistForm/WaitlistForm';
import WaitlistForm from '@/components/Forms/WaitlistForm/WaitlistForm';
import MainLayout from '@/components/Layout/Main';
import { CustomMeta } from '@/components/Meta/CustomMeta';
import Button from '@/components/Primitive/Button/Button';
import SmartLink from '@/components/Primitive/SmartLink';

import postApi from '../src/lib/api-poster';

interface IPlayNowCard {
  title: string;
  description: string;
  children: React.ReactNode;
  className?: string;
}

const PlayNowCard = ({
  title,
  description,
  children,
  className,
}: IPlayNowCard) => {
  return (
    <div
      className={clsx(
        'preserve-3d relative m-1 flex flex-col md:min-h-[400px]',
        className
      )}
    >
      <div className="preserve-3d absolute z-[-1] h-full w-full rounded-2xl">
        <div className="absolute left-[-2px] top-[-2px] h-[calc(100%+4px)] w-[calc(100%+4px)] rounded-2xl border-8 border-neonBlue-500 blur-[3px] transition-all" />
        <div className="absolute h-full w-full rounded-2xl border-4 border-white transition-all" />
        <div className="absolute z-[-1] h-full w-full rounded-2xl blur-md backdrop-blur-sm" />
      </div>

      <div className="z-10 flex h-full grow flex-col items-center justify-center space-y-3 p-6 text-center md:pt-10">
        <h2 className="font-dharma text-5xl font-black italic leading-none text-cream md:text-6xl">
          {title}
        </h2>
        <p className="font-medium text-cream">{description}</p>
        <div className="mt-auto flex w-full grow flex-col items-center justify-center">
          {children}
        </div>
      </div>
    </div>
  );
};

const PlayNow = () => {
  const [showWhitelistForm, setShowWhitelistForm] = React.useState(false);

  const [formError, setFormError] = React.useState('');
  const [formSuccess, setFormSuccess] = React.useState(false);
  const [formSubmitting, setFormSubmitting] = React.useState(false);

  const onSubmit = async (formData: WaitlistFormData) => {
    setFormError('');
    setFormSuccess(false);
    setFormSubmitting(true);

    const res = await postApi(`/api/waitlist-form`, formData);
    if (res.status !== 200) {
      setFormError(
        'There was an error submitting your message. Please try again later.'
      );
      setFormSubmitting(false);
      return false;
    }
    setFormSuccess(true);
    setFormSubmitting(false);
    return true;
  };

  return (
    <MainLayout withNavSpacer>
      <CustomMeta title="Play Now" />
      <div className="container-lg overflow-hidden">
        <SectionTitle intro="TIME TO" title="PLAY" />
        <Image
          src={playNowHero}
          alt="Play now"
          width={1348}
          height={557}
          placeholder="blur"
          className="hidden pb-8 sm:block sm:pb-4"
        />
        <Image
          src={playNowHeroMobile}
          alt="Play now"
          width={600}
          height={600}
          placeholder="blur"
          className="pb-8 sm:hidden sm:pb-4"
        />
      </div>
      <div className="overflow-hidden">
        <div className="container-md pb-page flex flex-col space-y-8">
          <div className="perspective-1200 mt-10 flex flex-col gap-4 md:flex-row lg:gap-6">
            <div className="md:rotate-y-25 grow-0 basis-1/3">
              <Slide left>
                <PlayNowCard
                  className=""
                  title="US & CANADA"
                  description="Find us on Google Play and the Apple App Store."
                >
                  <div className="flex flex-col items-center space-y-3">
                    <SmartLink
                      href="https://play.google.com/store/apps/details?id=com.portalone.arcade&hl=en&gl=US"
                      target="_blank"
                      className="hover-scale block w-full max-w-[150px]"
                    >
                      <Image
                        src={googlePlayImg}
                        alt="Google Play"
                        width={182}
                        height={54}
                        placeholder="blur"
                      />
                    </SmartLink>
                    <SmartLink
                      href="https://apps.apple.com/us/app/portalone-arcade/id1495839430"
                      target="_blank"
                      className="hover-scale block w-full max-w-[150px]"
                    >
                      <Image
                        src={appleStoreImg}
                        alt="Apple Store"
                        width={159}
                        height={51}
                        placeholder="blur"
                      />
                    </SmartLink>
                  </div>
                </PlayNowCard>
              </Slide>
            </div>
            <Slide bottom>
              <PlayNowCard
                className="grow-0 basis-1/3 md:scale-95"
                title="NORWAY"
                description="Find us on Google Play and the Apple App Store."
              >
                <div className="flex flex-col items-center space-y-3">
                  <SmartLink
                    href="https://play.google.com/store/apps/details?id=com.portalone.arcade"
                    target="_blank"
                    className="hover-scale block w-full max-w-[150px]"
                  >
                    <Image
                      src={googlePlayImg}
                      alt="Google Play"
                      width={182}
                      height={54}
                      placeholder="blur"
                    />
                  </SmartLink>
                  <SmartLink
                    href="https://apps.apple.com/no/app/portalone-arcade-norway/id1495839430"
                    target="_blank"
                    className="hover-scale block w-full max-w-[150px]"
                  >
                    <Image
                      src={appleStoreImg}
                      alt="Apple Store"
                      width={159}
                      height={51}
                      placeholder="blur"
                    />
                  </SmartLink>
                </div>
              </PlayNowCard>
            </Slide>

            <div className="md:-rotate-y-25 grow-0 basis-1/3">
              <Slide right>
                <PlayNowCard
                  title="REST OF WORLD"
                  description="PortalOne is expanding! Join our waitlist to be the first to know when we’re coming to your country."
                >
                  <Button
                    onClick={() => setShowWhitelistForm(true)}
                    className="uppercase"
                  >
                    Join waitlist
                  </Button>
                </PlayNowCard>
              </Slide>
            </div>
          </div>
        </div>
      </div>

      <WaitlistForm
        open={showWhitelistForm}
        onClose={() => setShowWhitelistForm(false)}
        onSubmit={onSubmit}
        error={formError}
        isLoading={formSubmitting}
        isSuccess={formSuccess}
      />
    </MainLayout>
  );
};

export default PlayNow;
